import { KeyboardEvent, ReactNode, useId, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./Tabs.module.css";

export type Tab = {
  key: string;
  label: string;
  content: ReactNode;
};

type TabsProps = {
  tabs: Tab[];
  label: string;
  queryParamKey?: string;
};

// Tabs after the WAI-ARIA pattern: arrows, Home and End move focus and selection, the selected tab is in the URL.
const Tabs = ({ tabs, label, queryParamKey = "tab" }: TabsProps) => {
  const id = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const fromUrl = tabs.findIndex((tab) => tab.key === searchParams.get(queryParamKey));
  const selected = fromUrl === -1 ? 0 : fromUrl;

  const select = (index: number, focus = false) => {
    const next = new URLSearchParams(searchParams);
    next.set(queryParamKey, tabs[index].key);
    setSearchParams(next, { replace: true });
    if (focus) {
      tabRefs.current[index]?.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = tabs.length - 1;
    const keys: Record<string, number> = {
      ArrowRight: selected === last ? 0 : selected + 1,
      ArrowLeft: selected === 0 ? last : selected - 1,
      Home: 0,
      End: last,
    };
    if (event.key in keys) {
      event.preventDefault();
      select(keys[event.key], true);
    }
  };

  const tabId = (key: string) => `${id}-tab-${key}`;
  const panelId = (key: string) => `${id}-panel-${key}`;
  const current = tabs[selected];

  return (
    <div className={styles.root}>
      <div className={styles.list} role='tablist' aria-label={label}>
        {tabs.map((tab, index) => (
          <button
            key={tab.key}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            className={styles.tab}
            id={tabId(tab.key)}
            type='button'
            role='tab'
            aria-selected={index === selected}
            aria-controls={panelId(tab.key)}
            tabIndex={index === selected ? 0 : -1}
            onClick={() => select(index)}
            onKeyDown={handleKeyDown}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {current && (
        <div className={styles.panel} id={panelId(current.key)} role='tabpanel' aria-labelledby={tabId(current.key)} tabIndex={0}>
          {current.content}
        </div>
      )}
    </div>
  );
};

export default Tabs;
